import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import UnifiedMenu from '../components/UnifiedMenu';
import { X, Music as MusicIcon } from 'lucide-react';
import { toast } from 'sonner';
import { MUSIC_GENRES, FAVORITE_ARTISTS, GENRE_ARTIST_MAP } from '../translations';
import { useLanguage } from '../contexts/LanguageContext';

export default function MusicEnhanced({ user, onLogout }) {
  const { t, language } = useLanguage();
  const [currentMood, setCurrentMood] = useState(null);
  const [musicRecs, setMusicRecs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedSong, setSelectedSong] = useState(null);
  
  // Filters
  const [selectedLanguage, setSelectedLanguage] = useState(user?.language || 'en');
  const [selectedEra, setSelectedEra] = useState('current');
  const [selectedGenre, setSelectedGenre] = useState('all');
  const [selectedArtist, setSelectedArtist] = useState('all');
  const [selectedMood, setSelectedMood] = useState('auto');

  const languages = [
    { code: 'en', name: 'English' }, { code: 'hi', name: 'Hindi' }, 
    { code: 'es', name: 'Spanish' }, { code: 'fr', name: 'French' },
    { code: 'de', name: 'German' }, { code: 'ar', name: 'Arabic' }
  ];

  const eras = [
    { value: 'current', label: 'Current' },
    { value: '2010s', label: '2010s' },
    { value: '2000s', label: '2000s' },
    { value: '90s', label: '90s' },
    { value: '80s', label: '80s' },
    { value: 'classic', label: 'Classic' }
  ];

  const getGenreOptions = () => {
    const all = { value: 'all', label: 'All Genres' };
    const userGenres = MUSIC_GENRES.filter(g => user?.music_preferences?.includes(g.value));
    const others = MUSIC_GENRES.filter(g => !user?.music_preferences?.includes(g.value));
    return [all, ...userGenres, ...others];
  };

  const getArtistOptions = () => {
    const all = { value: 'all', label: 'All Artists' };
    let artists = [];
    
    if (selectedGenre !== 'all') {
      const genreArtists = GENRE_ARTIST_MAP[selectedGenre] || [];
      artists = FAVORITE_ARTISTS.filter(a => genreArtists.includes(a.value));
    } else {
      const userArtists = FAVORITE_ARTISTS.filter(a => user?.artist_preferences?.includes(a.value));
      const others = FAVORITE_ARTISTS.filter(a => !user?.artist_preferences?.includes(a.value));
      artists = [...userArtists, ...others];
    }
    
    return [all, ...artists];
  };

  useEffect(() => {
    fetchMood();
  }, []);

  useEffect(() => {
    if (currentMood) fetchMusic();
  }, [selectedLanguage, selectedEra, selectedGenre, selectedArtist, selectedMood]);

  const fetchMood = async () => {
    try {
      const res = await axios.get(`${API}/moods?limit=1`);
      const mood = res.data?.[0]?.primary_category || 'Grateful';
      setCurrentMood(mood);
      fetchMusic(mood);
    } catch {
      setCurrentMood('Grateful');
      fetchMusic('Grateful');
    }
  };

  const fetchMusic = async (mood = currentMood) => {
    setLoading(true);
    try {
      const actualMood = selectedMood === 'auto' ? (mood || 'Grateful') : selectedMood;
      
      const res = await axios.get(`${API}/music/recommendations/v2`, {
        params: {
          mood: actualMood,
          language: selectedLanguage,
          era: selectedEra,
          genre: selectedGenre !== 'all' ? selectedGenre : undefined,
          artist: selectedArtist !== 'all' ? selectedArtist : undefined
        }
      });
      setMusicRecs(res.data);
    } catch (error) {
      console.error('Music fetch failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSongClick = (song) => {
    setSelectedSong(song);
  };

  const openPlatform = (platform) => {
    if (!selectedSong) return;
    const url = platform === 'youtube' ? selectedSong.youtube_url : selectedSong.spotify_url;
    window.open(url, '_blank');
    setSelectedSong(null);
    toast.success(`Opening on ${platform === 'youtube' ? 'YouTube' : 'Spotify'}! 🎵`);
  };

  return (
    <div className="h-screen flex flex-col bg-gradient-to-b from-white via-stone-50 to-stone-100 overflow-hidden">
      <UnifiedMenu active="music" stats={null} currentQuote="" recentMoods={[]} onLogout={onLogout} />
      
      {/* Fixed Header with Filters */}
      <div className="flex-none px-4 py-3 bg-white border-b border-stone-200">
        <h1 className="text-xl font-semibold text-warm-charcoal mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
          {t('music')}
        </h1>
        
        {/* Compact Filters - 6 dropdowns including Mood */}
        <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
          <select value={selectedMood} onChange={(e) => setSelectedMood(e.target.value)}
                  className="text-xs px-2 py-1.5 rounded-lg border border-stone-200">
            <option value="auto">{t('auto_mood') || 'Auto (Mood)'}</option>
            <option value="happy">{t('feeling_happy') || 'Happy'}</option>
            <option value="sad">{t('feeling_sad') || 'Sad'}</option>
            <option value="calm">{t('feeling_calm') || 'Calm'}</option>
            <option value="energetic">{t('feeling_energized') || 'Energetic'}</option>
          </select>
          <select value={selectedGenre} onChange={(e) => { setSelectedGenre(e.target.value); setSelectedArtist('all'); }}
                  className="text-xs px-2 py-1.5 rounded-lg border border-stone-200">
            {getGenreOptions().map(g => <option key={g.value} value={g.value}>{g.label} {user?.music_preferences?.includes(g.value) ? '★' : ''}</option>)}
          </select>
          <select value={selectedArtist} onChange={(e) => setSelectedArtist(e.target.value)}
                  className="text-xs px-2 py-1.5 rounded-lg border border-stone-200">
            {getArtistOptions().map(a => <option key={a.value} value={a.value}>{a.label} {user?.artist_preferences?.includes(a.value) ? '★' : ''}</option>)}
          </select>
          <select value={selectedLanguage} onChange={(e) => setSelectedLanguage(e.target.value)}
                  className="text-xs px-2 py-1.5 rounded-lg border border-stone-200">
            {languages.map(l => <option key={l.code} value={l.code}>{l.name}</option>)}
          </select>
          <select value={selectedEra} onChange={(e) => setSelectedEra(e.target.value)}
                  className="text-xs px-2 py-1.5 rounded-lg border border-stone-200">
            {eras.map(e => <option key={e.value} value={e.value}>{e.label}</option>)}
          </select>
          <button onClick={fetchMood} className="text-xs px-2 py-1.5 bg-hunter-green text-white rounded-lg">
            {t('refresh') || 'Refresh'}
          </button>
        </div>
      </div>

      {/* Scrollable Song List */}
      <div className="flex-1 overflow-y-auto px-4 py-3">
        {loading && <p className="text-center text-sm text-warm-charcoal/60 py-8">Loading...</p>}
        
        {!loading && musicRecs?.popular_songs && musicRecs.popular_songs.length === 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 text-center">
            <p className="text-sm font-semibold text-amber-900 mb-2">
              No songs found for this combination
            </p>
            <p className="text-xs text-amber-700 mb-4">
              {selectedGenre !== 'all' && selectedEra === 'current' 
                ? `${selectedGenre.charAt(0).toUpperCase() + selectedGenre.slice(1)} doesn't have current era songs.`
                : `Not enough popular music for: ${selectedGenre} in ${selectedEra} era.`
              }
            </p>
            <p className="text-xs text-warm-charcoal/60 mb-3">Would you like to:</p>
            <div className="flex gap-2 justify-center">
              <button 
                onClick={() => setSelectedEra('classic')}
                className="px-4 py-2 bg-white border-2 border-amber-300 text-amber-900 rounded-lg text-xs font-medium hover:bg-amber-50"
              >
                Try {selectedGenre === 'ghazal' ? 'Classic' : '80s/90s'}
              </button>
              <button 
                onClick={() => setSelectedGenre('all')}
                className="px-4 py-2 bg-white border-2 border-amber-300 text-amber-900 rounded-lg text-xs font-medium hover:bg-amber-50"
              >
                Try All Genres
              </button>
            </div>
          </div>
        )}
        
        {!loading && musicRecs?.popular_songs && musicRecs.popular_songs.length > 0 && (
          <>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-sm">{musicRecs.popular_songs.length} {t('songs') || 'Songs'}</h3>
              <span className="text-xs text-warm-charcoal/50">{selectedLanguage} • {selectedEra}</span>
            </div>
            
            <div className="space-y-2">
              {musicRecs.popular_songs.map((song, idx) => (
                <button
                  key={idx}
                  onClick={() => handleSongClick(song)}
                  className="w-full bg-white p-3 rounded-xl border border-stone-200 hover:border-hunter-green hover:shadow-md transition-all text-left group"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold text-sm text-warm-charcoal truncate group-hover:text-hunter-green">{song.title}</h4>
                      <p className="text-xs text-warm-charcoal/60 mt-0.5">{song.artist} • {song.year}</p>
                    </div>
                    <div className="ml-3 flex items-center gap-1 flex-shrink-0">
                      <MusicIcon className="w-4 h-4 text-hunter-green" />
                      <span className="text-xs text-warm-charcoal/40">{t('tap_to_play') || 'Tap to play'}</span>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Platform Selection Modal */}
      {selectedSong && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setSelectedSong(null)}>
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-lg">{t('play_on') || 'Play On'}</h3>
              <button onClick={() => setSelectedSong(null)} className="p-1 hover:bg-stone-100 rounded-full">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <p className="text-sm text-warm-charcoal/70 mb-4">
              <span className="font-semibold">{selectedSong.title}</span><br/>
              <span className="text-xs">{selectedSong.artist}</span>
            </p>
            
            <div className="space-y-2">
              <button
                onClick={() => openPlatform('youtube')}
                className="w-full flex items-center justify-center gap-3 py-4 bg-red-500 hover:bg-red-600 text-white rounded-xl font-semibold transition-all"
              >
                <MusicIcon className="w-5 h-5" />
                <span>YouTube Music</span>
              </button>
              <button
                onClick={() => openPlatform('spotify')}
                className="w-full flex items-center justify-center gap-3 py-4 bg-green-500 hover:bg-green-600 text-white rounded-xl font-semibold transition-all"
              >
                <MusicIcon className="w-5 h-5" />
                <span>Spotify</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
